import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField
} from '@mui/material';
import { useGetList, useNotify, useUpdate } from 'react-admin';
import { getErrorMessage } from '../utils/helperUtils.ts';
import { COVERAGE_LINES, type CoverageLine, type Renewal } from './renewalApi.ts';

// Every field of one renewal, and the deal working it: one of its contact's.
// Adding one inline only asks for three.
export const RenewalEditDialog = ({
  renewal,
  onClose,
  onSaved
}: {
  renewal: Renewal | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const notify = useNotify();
  const [update, { isPending }] = useUpdate();
  const [coverage, setCoverage] = useState<CoverageLine>('PACKAGE');
  const [renewalDate, setRenewalDate] = useState('');
  const [carrier, setCarrier] = useState('');
  const [premium, setPremium] = useState('');
  const [notes, setNotes] = useState('');
  const [opportunityId, setOpportunityId] = useState<number | ''>('');
  const { data: deals = [] } = useGetList<{ id: number; dealName: string | null }>(
    'opportunities',
    {
      filter: { contactId: renewal?.contactId },
      sort: { field: 'createdAt', order: 'DESC' },
      pagination: { page: 1, perPage: 50 }
    },
    { enabled: renewal !== null }
  );

  useEffect(() => {
    if (!renewal) return;
    setOpportunityId(renewal.opportunityId ?? '');
    setCoverage(renewal.coverage);
    setRenewalDate(renewal.renewalDate);
    setCarrier(renewal.incumbentCarrier ?? '');
    setPremium(renewal.estimatedPremium === null ? '' : String(renewal.estimatedPremium));
    setNotes(renewal.notes ?? '');
  }, [renewal]);

  const premiumValue = premium.trim() === '' ? null : Number(premium);
  const premiumInvalid =
    premiumValue !== null && (!Number.isFinite(premiumValue) || premiumValue < 0);

  const save = () => {
    if (!renewal) return;
    update(
      'renewals',
      {
        id: renewal.id,
        data: {
          opportunityId: opportunityId === '' ? null : opportunityId,
          coverage,
          renewalDate,
          incumbentCarrier: carrier.trim() || null,
          estimatedPremium: premiumValue === null ? null : Math.round(premiumValue * 100) / 100,
          notes: notes.trim() || null
        },
        previousData: renewal
      },
      {
        onSuccess: () => {
          notify('Renewal saved', { type: 'info' });
          onSaved();
          onClose();
        },
        onError: (error) => notify(getErrorMessage(error), { type: 'warning' })
      }
    );
  };

  return (
    <Dialog open={renewal !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit renewal</DialogTitle>
      <DialogContent>
        <TextField
          select
          fullWidth
          margin="normal"
          label="Line of coverage"
          value={coverage}
          onChange={(event) => setCoverage(event.target.value as CoverageLine)}
        >
          {COVERAGE_LINES.map((line) => (
            <MenuItem key={line.value} value={line.value}>
              {line.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          margin="normal"
          type="date"
          label="Renewal date"
          value={renewalDate}
          onChange={(event) => setRenewalDate(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Incumbent carrier"
          value={carrier}
          onChange={(event) => setCarrier(event.target.value)}
        />
        <TextField
          fullWidth
          margin="normal"
          type="number"
          label="Estimated premium"
          value={premium}
          onChange={(event) => setPremium(event.target.value)}
          error={premiumInvalid}
          helperText={premiumInvalid ? 'Enter an amount in dollars' : 'For sizing, not accounting'}
          slotProps={{
            input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
            htmlInput: { min: 0, step: 100 }
          }}
        />
        <TextField
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          label="Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        {deals.length ? (
          <TextField
            select
            fullWidth
            margin="normal"
            label="Deal"
            value={opportunityId}
            onChange={(event) => setOpportunityId(Number(event.target.value) || '')}
            helperText="The deal working this renewal"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {deals.map((deal) => (
              <MenuItem key={deal.id} value={deal.id}>
                {deal.dealName || `Deal ${deal.id}`}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={isPending || !renewalDate || premiumInvalid}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
